import { ChevronDown, ChevronUp, ExternalLink } from "@/components/ui/icons";
import { useState } from "react";
import { Linking, Pressable, Text, View } from "react-native";

import { AltSoldsLink } from "@/components/market/AltSoldsLink";
import { EbaySoldsLink } from "@/components/market/EbaySoldsLink";
import { FanaticsSoldsLink } from "@/components/market/FanaticsSoldsLink";
import { PriceStack, quoteForSource } from "@/components/market/PriceStack";
import { cardRecentSales, RecentSales } from "@/components/market/RecentSales";
import type { PublicItemDetail } from "@/hooks/usePublicBrowse";
import { priceChartingProductUrl } from "@/lib/catalog/priceCharting";
import { tcgplayerUrl } from "@/lib/catalog/tcgplayer";
import { formatMoney } from "@/lib/money/money";
import { altSoldsSearch } from "@/lib/scan/altSolds";
import { ebaySoldsUrl } from "@/lib/scan/ebaySolds";
import { fanaticsSoldsSearch } from "@/lib/scan/fanaticsSolds";
import { useThemeColors } from "@/theme/theme";

const openListing = (url: string) => {
  if (url) Linking.openURL(url).catch(() => {});
};

const CONDITIONS = ["NM", "LP", "MP", "HP", "DMG"] as const;

/** The scope a public card's comps are read in: the card's own, else the first offered. */
function subjectScope(detail: PublicItemDetail) {
  const lastSolds = detail.last_solds;
  return lastSolds?.scopes.find((s) => s.scope === lastSolds.subject_scope)
    ?? lastSolds?.scopes[0]
    ?? null;
}

/** Whether the unit is a raw single, whose comps depend on its recorded condition (TRO-827). */
export function isRawCopy(detail: PublicItemDetail): boolean {
  const { item } = detail;
  return !item.is_graded && item.product_type !== "sealed"
    && subjectScope(detail)?.scope !== "Sealed";
}

/**
 * "Compare prices" on a public card page: what the market says about the card a vendor is
 * selling, one tap below the vendor's own price. A table's card detail and a shop's card page
 * share it, so a customer compares the same way in either room.
 *
 * Open, it shows the Dossier's per-source price stack (TRO-775), the TCGplayer market row
 * (always, even with no last-solds, TRO-483), eBay and Fanatics sold-history searches
 * (the same links as Scan and the vendor's card detail) and the recent sales. A raw copy with no recorded
 * condition shows no quotes or sales rather than Near Mint ones (TRO-827). A public page reports
 * nothing to the vendor app's analytics, so the eBay link does not track.
 */
export function CardComparePrices({ detail }: { detail: PublicItemDetail; }) {
  const c = useThemeColors();
  const [open, setOpen] = useState(false);
  const { item, last_solds } = detail;
  const stackScope = subjectScope(detail);
  const unknownCondition = isRawCopy(detail) && !item.condition;
  const quotes = unknownCondition ? [] : stackScope?.quotes ?? [];
  const sales = cardRecentSales(
    unknownCondition ? [] : stackScope?.sales ?? [],
    unknownCondition ? null : stackScope?.misprint,
  );
  // With a resolved TCGplayer product id it opens the exact product page; otherwise it searches by
  // name and set, preferring the catalog identity's name and set over the unit's.
  const marketPrice = quoteForSource(quotes, "tcgplayer")?.price ?? null;
  const marketUrl = tcgplayerUrl(
    last_solds?.name ?? item.product_name,
    last_solds?.set_name ?? item.set_name,
    detail.tcgplayer_product_id,
  );
  // Sealed product is often a PriceCharting identity with a row in the Sealed scope (TRO-804). The
  // public payload has no `source_url`, so build the link from the identity to keep the row
  // pressable.
  const priceChartingUrl = priceChartingProductUrl(
    last_solds?.system,
    last_solds?.name,
    last_solds?.set_name,
  );
  const name = last_solds?.name ?? item.product_name;
  const condition = CONDITIONS.find((code) => code === item.condition) ?? null;
  const ebayUrl = ebaySoldsUrl({
    game: item.game,
    name,
    number: item.number,
    setName: last_solds?.set_name ?? item.set_name,
    printing: item.variant_label,
    graded: item.is_graded,
    condition,
    gradingCompany: item.grading_company,
    grade: item.grade,
  });
  const fanatics = item.product_type !== "sealed" && stackScope?.scope !== "Sealed"
    ? fanaticsSoldsSearch({
      name,
      number: item.number,
      setName: last_solds?.set_name ?? item.set_name,
      game: item.game,
      printing: item.variant_label,
      graded: item.is_graded,
      grade: item.grade,
      gradingCompany: item.grading_company,
    })
    : null;
  const alt = item.product_type !== "sealed" && stackScope?.scope !== "Sealed"
    ? altSoldsSearch({
      name,
      number: item.number,
      setName: last_solds?.set_name ?? item.set_name,
      game: item.game,
      printing: item.variant_label,
      graded: item.is_graded,
      grade: item.grade,
      gradingCompany: item.grading_company,
    })
    : null;

  return (
    <>
      <Pressable
        testID="browse-detail-compare"
        onPress={() => setOpen((was) => !was)}
        accessibilityRole="button"
        aria-expanded={open}
        accessibilityState={{ expanded: open }}
        className="min-h-11 flex-row items-center justify-between rounded-xl border border-border bg-surface px-4"
      >
        <Text className="font-label text-sm text-foreground">Compare prices</Text>
        {open
          ? <ChevronUp size={18} color={c.mutedForeground} />
          : <ChevronDown size={18} color={c.mutedForeground} />}
      </Pressable>
      {open
        ? (
          // `last_solds` is null for a generic unit, which is an empty state, not an error.
          <View className="gap-2">
            {stackScope
              ? (
                <PriceStack
                  testID="browse-detail-price-stack"
                  quotes={quotes}
                  scope={stackScope.scope}
                  misprint={unknownCondition ? null : stackScope.misprint}
                  links={{ tcgplayer: marketUrl, pricecharting: priceChartingUrl ?? undefined }}
                  onOpen={openListing}
                />
              )
              : null}
            {/* The comps section heading, part of the screen-reader outline (#531). */}
            <Text
              className="text-xs font-label uppercase tracking-widest text-subtle"
              accessibilityRole="header"
              // A section under the card name; without a level, react-native-web renders a second
              // `<h1>`.
              aria-level={2}
            >
              Market comps
            </Text>
            <Pressable
              testID="browse-detail-tcgplayer"
              onPress={() => openListing(marketUrl)}
              accessibilityRole="link"
              accessibilityLabel={marketPrice != null
                ? `View ${item.product_name} on TCGplayer — market ${formatMoney(marketPrice)}`
                : `View ${item.product_name} on TCGplayer`}
              className="flex-row items-center justify-between rounded-xl border border-border bg-surface px-4 py-3 hover:bg-surface-muted active:bg-surface-muted"
            >
              <View className="flex-row items-center gap-1.5">
                <Text className="text-sm font-body text-muted-foreground">TCGplayer market</Text>
                <ExternalLink size={13} color={c.subtle} />
              </View>
              {marketPrice != null
                ? (
                  <Text className="font-num-bold text-base text-foreground">
                    {formatMoney(marketPrice)}
                  </Text>
                )
                : <Text className="text-sm font-label text-muted-foreground">View</Text>}
            </Pressable>
            <EbaySoldsLink
              testID="browse-detail-ebay-solds"
              url={ebayUrl}
              name={name}
              graded={item.is_graded}
              condition={condition}
              printing={item.variant_label}
              track={false}
            />
            {fanatics
              ? (
                <FanaticsSoldsLink
                  search={fanatics}
                  testID="browse-detail-fanatics-solds"
                  track={false}
                />
              )
              : null}
            {alt
              ? (
                <AltSoldsLink
                  search={alt}
                  testID="browse-detail-alt-solds"
                  track={false}
                />
              )
              : null}
            <RecentSales key={item.public_id} sales={sales} />
          </View>
        )
        : null}
    </>
  );
}
